/**
 * useFeedback — submit a feedback record from any LexIDE surface.
 *
 * Responsibilities:
 *  1. Merge ambient context (FeedbackProvider) with the call-site override.
 *  2. Build the `clientMeta` block (appVersion, userAgent, locale, viewport).
 *  3. Optionally sanitize free-text (CPF/CNPJ/email redaction) — caller
 *     opts in/out via `sanitize` arg.
 *  4. POST to lexserver `/api/feedback`.
 *  5. On transient failure (network / 5xx / no JWT), enqueue to the outbox
 *     so a later retry can land it.
 *
 * Outbox flush is driven by a single `<FeedbackOutboxRunner />` mounted at
 * the app shell — NOT by this hook. Putting the flush effect in every
 * useFeedback consumer caused N parallel timers; the central runner is
 * the single source of truth.
 *
 * Returns:
 *  { submit, isSubmitting, lastError, queuedCount }
 */

import { useCallback, useState } from "react";
import { submitFeedback } from "@/lib/lexserver-client";
import { buildClientMeta, mergeSurfaceContext } from "@/lib/feedback-context";
import { sanitizeFeedbackText } from "@/lib/feedback-sanitize";
import { useFeedbackStore, type QueuedFeedback } from "@/stores/useFeedbackStore";
import { useAmbientFeedbackContext } from "@/components/feedback/FeedbackProvider";
import type {
  CreateFeedbackInput,
  CreatedFeedback,
  FeedbackPolarity,
  FeedbackSurface,
  FeedbackSurfaceContext,
} from "@/types/feedback";

/** Max attempts before the outbox drops a queued item as un-deliverable.
 *  Exported so the unit-test can reference it directly instead of
 *  hardcoding the cap (which would silently drift if this constant
 *  ever changes). */
export const MAX_OUTBOX_ATTEMPTS = 10;

interface SubmitArgs {
  polarity: FeedbackPolarity;
  surface: FeedbackSurface;
  /** Per-call context (merged with ambient). */
  context?: FeedbackSurfaceContext;
  comment?: string;
  /** Default true — set false when user explicitly opts out. */
  sanitize?: boolean;
}

interface UseFeedbackResult {
  submit: (args: SubmitArgs) => Promise<CreatedFeedback | null>;
  isSubmitting: boolean;
  lastError: string | null;
  queuedCount: number;
}

function genClientId(): string {
  // Best-effort UUID — globalThis.crypto.randomUUID is widely supported.
  // Fallback keeps test environments and old browsers working.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `cid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Drain the outbox best-effort. Used by `<FeedbackOutboxRunner />` and the
 * 5-minute retry timer; safe to call repeatedly. Skipped when the device is
 * offline or another flush is already in flight.
 *
 * Per-item handling:
 *  - 4xx (validation / dedup): drop the item — it can never succeed.
 *  - 5xx / network: touch (bumps attempts + lastAttemptAt). If attempts
 *    exceed MAX_OUTBOX_ATTEMPTS, drop the item to keep the queue bounded.
 *  - Otherwise: continue — a transient failure on one item should not
 *    halt the rest of the queue (different items may have different
 *    failure modes; e.g. one body is too large but the rest are fine).
 */
export async function flushFeedbackOutbox(): Promise<void> {
  const initial = useFeedbackStore.getState();
  if (initial.isFlushing) return;
  if (initial.outbox.length === 0) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  const { ack, touch, setFlushing } = initial;
  setFlushing(true);
  try {
    // Iterate over a snapshot — the store mutates as we go.
    const snapshot = initial.outbox.slice();
    for (const item of snapshot) {
      try {
        await submitFeedback(item.payload as CreateFeedbackInput);
        ack(item.clientId);
      } catch (err) {
        const status = (err as { status?: number }).status;
        // 4xx (validation / dedup) — record can never succeed; drop.
        if (typeof status === "number" && status >= 400 && status < 500) {
          ack(item.clientId);
          continue;
        }
        // Transient (5xx / network). Bump attempts; drop after the cap so
        // the queue stays bounded even when the server is permanently
        // unreachable for a given payload shape.
        if (item.attempts + 1 >= MAX_OUTBOX_ATTEMPTS) {
          ack(item.clientId);
          continue;
        }
        touch(item.clientId);
        // Don't break — other items may have unrelated failure modes.
        continue;
      }
    }
  } finally {
    useFeedbackStore.getState().setFlushing(false);
  }
}

export function useFeedback(): UseFeedbackResult {
  const ambient = useAmbientFeedbackContext();
  const enqueue = useFeedbackStore((s) => s.enqueue);
  const queuedCount = useFeedbackStore((s) => s.outbox.length);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const submit = useCallback(
    async (args: SubmitArgs): Promise<CreatedFeedback | null> => {
      setIsSubmitting(true);
      setLastError(null);
      try {
        const surfaceContext = mergeSurfaceContext(ambient, args.context);
        const sanitize = args.sanitize !== false;
        let comment = args.comment?.trim() || undefined;
        if (comment && sanitize) {
          comment = sanitizeFeedbackText(comment).text;
        }
        // Apply the same sanitization to messageExcerpt when present.
        if (surfaceContext.messageExcerpt && sanitize) {
          surfaceContext.messageExcerpt = sanitizeFeedbackText(
            surfaceContext.messageExcerpt,
          ).text;
        }

        const payload: CreateFeedbackInput = {
          polarity: args.polarity,
          comment,
          surface: args.surface,
          surfaceContext,
          clientMeta: buildClientMeta(),
        };

        try {
          const result = await submitFeedback(payload);
          return result;
        } catch (err) {
          const e = err as { status?: number; code?: string; message?: string };
          // 409 dedup — treat as success (the prior record landed).
          if (e.status === 409) return null;
          // 4xx other than 409 — surface error, do NOT enqueue (will never succeed).
          if (typeof e.status === "number" && e.status >= 400 && e.status < 500) {
            setLastError(e.message ?? "Erro ao enviar feedback");
            return null;
          }
          // Network / 5xx — enqueue for retry.
          const queued: QueuedFeedback = {
            clientId: genClientId(),
            attempts: 1,
            lastAttemptAt: Date.now(),
            payload: payload as unknown as QueuedFeedback["payload"],
          };
          enqueue(queued);
          setLastError(null); // queued silently — no error surfaced to user
          return null;
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [ambient, enqueue],
  );

  return { submit, isSubmitting, lastError, queuedCount };
}
